/** 選手のピン（名前・順位・色・状態アイコン。docs/05_rendering-and-audio.md 8 章）。画面上で一定の大きさ */
import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';
import type { AthleteMode, AthleteState, Standing } from '../../sim/types';

export const STATE_GLYPH: Record<AthleteMode, string> = {
  idle: '止',
  hiking: '歩',
  preparing: '準',
  waitingWind: '風',
  flying: '飛',
  packing: '収',
  bivouac: '泊',
  finished: '着',
};

export const STATE_LABEL_JA: Record<AthleteMode, string> = {
  idle: '待機',
  hiking: '歩行中',
  preparing: '離陸準備',
  waitingWind: '風待ち',
  flying: '飛行中',
  packing: '格納中',
  bivouac: 'ビバーク',
  finished: 'ゴール',
};

interface Pin {
  object: CSS2DObject;
  rank: HTMLElement;
  state: HTMLElement;
  bubble: HTMLElement;
  bubbleUntil: number;
}

export class AthletePins {
  readonly group = new THREE.Group();
  private readonly pins = new Map<string, Pin>();

  /** onPick：ピンの名前をクリックしたとき（その選手をカメラで追う） */
  constructor(athletes: AthleteState[], onPick: (athleteId: string) => void = () => undefined) {
    for (const a of athletes) {
      const el = document.createElement('div');
      el.className = `athlete-pin${a.isPlayer ? ' athlete-pin--player' : ''}`;
      el.style.setProperty('--pin-color', a.color);
      const bubble = document.createElement('span');
      bubble.className = 'athlete-pin__bubble';
      bubble.hidden = true;
      const label = document.createElement('span');
      label.className = 'athlete-pin__name';
      const rank = document.createElement('b');
      rank.className = 'athlete-pin__rank';
      const name = document.createElement('span');
      name.textContent = a.isPlayer ? 'YOU' : a.name;
      const state = document.createElement('i');
      state.className = 'athlete-pin__state';
      label.append(rank, name, state);
      label.title = 'クリックでこの選手をカメラで追う';
      label.addEventListener('click', (e) => {
        e.stopPropagation();
        onPick(a.id);
      });
      const dot = document.createElement('span');
      dot.className = 'athlete-pin__dot';
      el.append(bubble, label, dot);
      const object = new CSS2DObject(el);
      object.center.set(0.5, 1);
      this.group.add(object);
      this.pins.set(a.id, { object, rank, state, bubble, bubbleUntil: 0 });
    }
  }

  /** visible を渡すと、それが false の選手のピンを隠す（追従カメラでは近くの選手だけ出す） */
  update(
    athletes: AthleteState[],
    standings: Standing[],
    nowMs: number,
    visible: (a: AthleteState) => boolean = () => true,
  ): void {
    const rankOf = new Map(standings.map((s) => [s.athleteId, s.rank]));
    for (const a of athletes) {
      const pin = this.pins.get(a.id);
      if (!pin) continue;
      pin.object.visible = visible(a);
      pin.object.position.set(a.pos.x, a.pos.y + 25, a.pos.z);
      pin.rank.textContent = String(rankOf.get(a.id) ?? '');
      if (pin.state.textContent !== STATE_GLYPH[a.mode]) {
        pin.state.textContent = STATE_GLYPH[a.mode];
        pin.state.title = STATE_LABEL_JA[a.mode];
      }
      if (!pin.bubble.hidden && nowMs > pin.bubbleUntil) pin.bubble.hidden = true;
    }
  }

  /** ライバルの発言をフキダシで出す（docs/06_ui-ux.md 8 章。本格的な連動は P3） */
  say(athleteId: string, text: string, nowMs: number): void {
    const pin = this.pins.get(athleteId);
    if (!pin) return;
    pin.bubble.textContent = text.length > 20 ? `${text.slice(0, 19)}…` : text;
    pin.bubble.hidden = false;
    pin.bubbleUntil = nowMs + 3500;
  }
}
